import React from "react";
import { api } from "../api";
import { Detail, DetailRow } from "./common";
import { AssignmentTable, CompoundTable } from "./MappingTable";
import { day } from "../lib/format";

/**
 * What was uploaded and what it was read as.
 *
 * Shown twice: once straight after an upload, as the panel the scientist checks
 * before committing to the run, and once when a stored run is reopened -- the
 * files are on the server by then, so step 1 shows what they were rather than an
 * empty form the run no longer needs.
 *
 * Each filename downloads the archived bytes. The instrument exports are the raw
 * record of the experiment, and having uploaded them is not a reason to have to
 * go back to the instrument to read them again.
 */
/**
 * A run carries two versions: the one that computed the numbers on screen, and
 * the one stamped when it was uploaded. They are the same until a release moves
 * the version, so showing both unconditionally would put a duplicate on every
 * freshly uploaded run. Shown only when they disagree, which is the only time
 * the distinction tells the scientist anything.
 */
function recomputedUnderANewerVersion(manifest) {
  return (
    Boolean(manifest.recorded_version) &&
    manifest.recorded_version !== manifest.analysis_version
  );
}

export default function RunInputs({ manifest, mapping }) {
  return (
    <>
      <div className="card">
        <h2>Run</h2>
        <DetailRow>
          <Detail label="Run">
            <span className="mono">{manifest.run_id}</span>
          </Detail>
          <Detail label="Operator">{manifest.metadata.operator}</Detail>
          <Detail label="Lab head">{manifest.metadata.lab_head}</Detail>
          <Detail label="Experiment start">{day(manifest.metadata.experiment_start)}</Detail>
          <Detail label="Experiment end">{day(manifest.metadata.experiment_end)}</Detail>
          <Detail label="Analysis version">{manifest.analysis_version}</Detail>
          {recomputedUnderANewerVersion(manifest) && (
            <Detail label="Recorded at upload">{manifest.recorded_version}</Detail>
          )}
        </DetailRow>
        {recomputedUnderANewerVersion(manifest) && (
          <p className="card-note">
            These results were computed by version {manifest.analysis_version}. The run was
            recorded under version {manifest.recorded_version} when it was uploaded. Whether
            the computation itself changed between the two is not recorded.
          </p>
        )}
        <h3 style={{ marginTop: "1rem" }}>Input files</h3>
        <table>
          <thead>
            <tr>
              <th>File</th>
              <th>Role</th>
              <th>SHA-256</th>
            </tr>
          </thead>
          <tbody>
            {manifest.inputs.map((f) => (
              <tr key={f.sha256}>
                <td>
                  <a
                    className="mono"
                    href={api.inputFileUrl(manifest.run_id, f.sha256)}
                    title={`Download ${f.filename} as uploaded`}
                  >
                    {f.filename}
                  </a>
                </td>
                <td>{f.role}</td>
                <td className="mono muted">{f.sha256.slice(0, 16)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Compounds as they will be fitted</h2>
        <p className="card-note">
          Replicate series are pooled into one curve per compound, and a compound run at more than
          one starting concentration contributes all of its blocks to a single fit.
        </p>
        <CompoundTable mapping={mapping} />
      </div>

      <div className="card">
        <h2>Series assignment</h2>
        <p className="card-note">
          Reconstructed by position from the Substanzen sheet: the Leerwert row is the blank and
          takes no number, and numbering starts at <span className="mono">Nr.2</span>.
        </p>
        <AssignmentTable mapping={mapping} />
      </div>
    </>
  );
}
